<template>
	<article class="my-workflow" :class="setWorkflowClass">
		<div class="my-workflow_meta subheading-1 mobile-caption-2">
      <div v-if="newWorkflow" >
        <span class="my-workflow_mark caption-small">NEW</span>
      </div>
			<header class="my-workflow_head">
        <time
          class="body-2 mobile-caption-small"
          :datetime="workflow.data.last_mod_date"
        >
          {{ setDateTime(workflow.data.last_mod_date) }}
        </time>
        <a
          class="my-workflow_title my-workflow_title-link"
          :href="workflowUrl"
          @mouseover="showTooltip"
          @mouseleave="closeTooltip"
          @click="$emit('viewMyWorkflow', workflow)"
        >
          {{ workflow.title }}
        </a>
			</header>
      <transition name="title_tooltip">
				<span
          v-if="tooltipInfo"
          class="title_tooltip wf-tooltip wf-tooltip-bottom"
          :style="{top: tooltipInfo.top, left: tooltipInfo.left}"
        >
          {{ tooltipInfo.title }}
        </span>
      </transition>
      <aside>
        <dropdown-custom
          :opener="openerDom"
        >
          <template v-slot:opener="buttonScope">
            <md-button
              ref="opener"
              @click="handleOpenerClick(buttonScope)"
              class="md-icon-button md-dense"
            >
              <base-icon class="options-opener" icon-name="more" icon-size="23px"/>
            </md-button>
          </template>
          <template v-slot:list="listScope">
            <base-dropdown-list
              :open="listScope.open"
              :close="listScope.closeDropDown"
              :options="moreOptions"
              :overflow-y-auto="true"

              :listWidth="320"
              :offset="-277"
              :option-data="workflow"
              :option-url="workflowUrl"
            />
          </template>
        </dropdown-custom>
      </aside>

		</div>
		<section class="my-workflow_steps">
			<div
        class="scrolling-wrapper"
        ref="scrollingWrapper"
        data-testid="scrolling-wrapper"
      >
				<ul class="steps-list">
          <li
            v-for="(step, index) in workflow.data.workflow_steps"
            :key="index"
          >
            <span class="steps-list_step caption">{{ getStepName(step) }}</span>
            <base-icon
              v-if="showBtnArrow(index, workflow.data.workflow_steps)"
              class="steps-list_icon"
              icon-name="arrow-forward"
            />
          </li>
				</ul>
			</div>
		</section>
		<option-modal-rename
      v-if="showedRenameModal"
      :current-workflow="workflow"
      @on-close="closeRenameModal"
    />
		<option-modal-delete
      v-if="showedDeleteModal"
      :current-workflow="workflow"
      @on-close="closeDeleteModal"
    />
	</article>
</template>

<script>
import {DateTime} from 'luxon';
import { getWebshopData } from '@/util/getWebshopData.js';
import { pdfReadFeature } from '@/util/pdfReadFeature.js';
import OptionModalRename from '@/components/myWorkflows/option-modal-rename.vue';
import OptionModalDelete from '@/components/myWorkflows/option-modal-delete.vue';
import saveWorkflow from '@/components/workflow/mixins/saveWorkflow';
import DropdownCustom from '@/components/dropdown-custom/DropdownCustom';
import BaseDropdownList from '@/components/Dropdown/BaseDropdown/BaseDropdownList.vue';

export default {
	components: { OptionModalRename, OptionModalDelete, DropdownCustom, BaseDropdownList },
	props: {
		workflow: {
			type: Object,
		},
	},
  mixins: [
    saveWorkflow,
  ],
	data() {
		return {
      openerDom: null,
			newWorkflow: false,
			isScrolling: false,
			tooltipInfo: null,
			showedRenameModal: false,
			showedDeleteModal: false,
      moreOptions: [
        {
          key: 'Rename',
          children: [],
          value: { enUS: 'Rename' },
          iconColor: '#8c8c8c',
					iconName: 'pen',
          callback: this.openRenameModal,
          disabled: false,
        },
        {
          key: 'MoreOptionsDuplicate',
          component: 'MoreOptionsDuplicate',
          children: [],
          value: { enUS: '' },
          disabled: false,
        },
        {
          key: 'Delete',
          children: [],
          value: { enUS: 'Delete' },
          iconColor: '#8c8c8c',
					iconName: 'delete',
          callback: this.openDeleteModal,
          disabled: false,
        },
        {
          key: 'MoreOptionsNewTab',
          component: 'MoreOptionsNewTab',
          children: [],
          value: { enUS: '' },
          disabled: false,
        },
        {
          key: 'Download as PDF',
          children: [],
          value: { enUS: 'Download as PDF' },
          iconColor: '#8c8c8c',
					iconName: 'download',
          callback: this.openWFInNewTab,
					disabledIconName: 'download',
          disabled: false,
          itemClass: 'mobile-hide'
        },
        {
          key: 'Download as CSV',
          children: [],
          value: { enUS: 'Download as CSV' },
          iconColor: '#8c8c8c',
					iconName: 'download',
          callback: () => this.downloadCSV(this.workflow.title),
					disabledIconName: 'download',
          disabled: false,
          itemClass: 'mobile-hide'
        },
				{
          key: 'MoreOptionsShareWorkflow',
          component: 'MoreOptionsShareWorkflow',
          children: [],
          value: { enUS: '' },
          disabled: true,
        },
      ]
		};
	},
	computed: {
    products() {
      return this.$store.state.productsList
    },
    renderWorkFlowSteps() {
      const country = this.$store.state.COUNTRY;
      return this.workflow.data.workflow_steps
        .map((step) => {
          const stepProducts = step.products ?? [];
          return {
            ...step,
            selected: step.selectedProduct,
            products: stepProducts.map((k) => {return k.customContent ?  k : getWebshopData(this.products[k], country)} )
          }
        });
    },
		workflowUrl(workflow) {
			const {
				data: {entryPoints},
			} = workflow.workflow;

			const resolvedUrl = this.$router.resolve({
				name: 'Workflow',
				params: {
					application: entryPoints.application,
					startingMaterial: entryPoints.startingMaterial,
					analyte: entryPoints.analyte,
					technology: entryPoints.technology,
				},
				query: {
					workflowId: workflow.workflow.id,
				},
			});
			return resolvedUrl.href;
		},
		setWorkflowClass() {
			return `workflow-${this.workflow.id}`;
		},
		getLang() {
			return this.$store.state.LANG || 'enUS';
		},
		getStepName() {
			return (wfs) => {
				if (this.$store.state.cvlWebSteps) {
					const checkForName = this.$store.state.cvlWebSteps.values.find((step) => step.key === wfs.name);
					return checkForName ? checkForName.value[this.getLang] : wfs.name;
				}
			};
		},
	},
	methods: {
    handleOpenerClick(openerScope) {
      openerScope.handleClick();
      this.openerDom = this.$refs.opener;
    },
    openWFInNewTab() {
      const url = this.workflowUrl.split('/workflow-configurator')[1];
      const routeData = this.$router.resolve({path: url});
      pdfReadFeature.enable();
      window.open(routeData.href, '_blank');
    },
		openRenameModal() {
			this.showedRenameModal = true;
		},
		closeRenameModal() {
			this.showedRenameModal = false;
		},
		openDeleteModal() {
			this.showedDeleteModal = true;
		},
		closeDeleteModal() {
			this.showedDeleteModal = false;
		},
    showTooltip(ctx) {
      const link = ctx.target;
      this.tooltipInfo = {
        title: link.innerText,
        left: `${link.offsetLeft}px`,
        top: `${link.offsetHeight + link.offsetTop}px`,
      };
    },
    closeTooltip() {
		  this.tooltipInfo = null;
    },
		setDateTime(dateTime) {
			return DateTime.fromISO(dateTime).toFormat('dd.LL.yyyy');
		},
		showBtnArrow(index, steps) {
			const element = index + 1;
			return element !== steps.length;
		},
		handleScrollListener() {
			const slider = this.$refs.scrollingWrapper;
			let isDown = false;
			let startX;
			let scrollLeft;

			slider.addEventListener('mousedown', (e) => {
				isDown = true;
				slider.classList.add('active');
				startX = e.pageX - slider.offsetLeft;
				scrollLeft = slider.scrollLeft;
			});
			slider.addEventListener('mouseleave', () => {
				isDown = false;
				slider.classList.remove('active');
			});
			slider.addEventListener('mouseup', () => {
				isDown = false;
				slider.classList.remove('active');
			});
			slider.addEventListener('mousemove', (e) => {
				if (!isDown) return;
				e.preventDefault();
				const x = e.pageX - slider.offsetLeft;
				const walk = (x - startX) * 3; // scroll-fast
				slider.scrollLeft = scrollLeft - walk;
			});
		},
	},
	mounted() {
		this.handleScrollListener();
  },
};
</script>

<style lang="scss" scoped>
$workflow-indent-side: 16px;

.my-workflow {
  background: $white;
  border-radius: 4px;
  margin-bottom: 28px;
  transition: box-shadow 350ms ease-in;
  @media (min-width: $tablet) {
    margin-bottom: 16px;
  }
  &:hover {
    box-shadow: 0 4px 8px rgba(8, 35, 48, .24);
  }
}
.my-workflow_meta {
  position: relative;
  display: flex;
  padding: 10px 0 13px $workflow-indent-side;
  border-bottom: 1px solid $color-light-grey;
  @media (min-width: $tablet) {
    align-items: center;
    padding: 7px 0 9px $workflow-indent-side;
  }
}
.my-workflow_mark {
  font-size: 13px;
  color: $color-blue;
  background: $color-extra-light-blue;
  letter-spacing: .4px;
  padding: 4px 8px;
  margin-right: 16px;
  border-radius: 4px;
}
.my-workflow_head {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (min-width: $tablet) {
    padding-right: 45px;
    align-items: center;
    flex-direction: row-reverse;
    justify-content: space-between;
  }
}
.my-workflow_title {
  overflow: hidden;
  padding-right: 7%;
  &:hover {
    + .title_tooltip {
      opacity: 1;
      transform: scale(0.95);
    }
  }
}
.my-workflow_title-link {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  @media (min-width: $tablet) {
    -webkit-line-clamp: 1;
  }
}
.title_tooltip {
  @include font-styles('Futura W01 Book', 400);
  @include textStyles(13, 20, 0.4);
  background-color: $color-tooltip-grey;
  color: $white;
  white-space: normal;
  padding: 0 8px;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 111;
  border-radius: 2px;
  transition: .15s cubic-bezier(0, 0, .2, 1);
  transition-property: opacity, transform;
  max-width: 75%;
  text-overflow: ellipsis;
  overflow: hidden;
  @media (min-width: $tablet) {
    max-width: 751px;
  }
}
.title_tooltip-leave-active {
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.title_tooltip-enter,
.title_tooltip-leave-active {
  opacity: 0;
  transform: scale(0);
  .wf-tooltip-top {
    transform: translate3d(0, 4px, 0) scale(0.95);
  }

  .wf-tooltip-right {
    transform: translate3d(-4px, 0, 0) scale(0.95);
  }

  .wf-tooltip-bottom {
    transform: translate3d(0, -4px, 0) scale(0.95);
  }

  .wf-tooltip-left {
    transform: translate3d(4px, 0, 0) scale(0.95);
  }
}

.my-workflow_steps {
  position: relative;
  padding: 12px 0 $workflow-indent-side/2;
  @media (min-width: $tablet) {
    padding: $workflow-indent-side 0;
  }
}
.scrolling-wrapper {
  user-select: none;
  cursor: pointer;
  overflow: -moz-scrollbars-none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;

	&:hover {
		overflow: scroll;
	}
  &:active {
    cursor: grabbing;
  }
  &::-webkit-scrollbar {
    width: 0 !important;
    display: none;
  }
  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 10;
    width: 20px;
    left: 0;
    background: linear-gradient(
      90deg,
      #fff 25%,
      rgba(#fff, .834254) 50%,
      rgba(#fff, 0) 100%
    );
  }
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 10;
    width: 20px;
    right: 0;
    background: linear-gradient(
      270deg,
      #fff 25%,
      rgba(#fff, .834254) 50%,
      rgba(#fff, 0) 100%
    );
  }
}

.steps-list {
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    align-items: center;
    &:first-child {
      margin-left: $workflow-indent-side;
    }
    &:last-child {
      margin-right: $workflow-indent-side;
    }
  }
}
.steps-list_step {
  background: $color-light-grey;
  border-radius: 8px;
  padding: 5px 16px;
}
.steps-list_icon {
  color: $color-mid-grey;
  margin: 0 20px;
}
</style>
